import { z } from 'zod'

export const MEMBERSHIP_MEMBERS_WRITE_CONTRACT_VERSION = '1.0.1' as const
const numericId = z.string().regex(/^[0-9]+$/)
const productKey = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/)
const roleKey = z.string().regex(/^[a-z][a-z0-9_-]{0,119}$/)

export const membershipMemberAddRequestSchema = z.object({
  userId: numericId,
  role: z.enum(['org-admin', 'org-member']),
  productGrants: z.array(z.object({ product: productKey, roleKey }).strict()).max(10).optional(),
}).strict()

export const membershipMemberStatusRequestSchema = z.object({
  role: z.enum(['org-admin', 'org-member']).optional(),
  status: z.enum(['active', 'suspended']).optional(),
  suspendedReason: z.string().trim().max(300).nullable().optional(),
}).strict().refine((value) => value.role !== undefined || value.status !== undefined, 'role or status is required').refine(
  (value) => value.status === 'suspended' || value.suspendedReason === undefined,
  'suspendedReason is only accepted when status is suspended',
)
export const membershipMemberRemoveRequestSchema = z.object({}).strict()
export const membershipMemberBulkSuspendRequestSchema = z.object({ userIds: z.array(numericId).min(1).max(100) }).strict()
export const membershipMemberResolveSeatsRequestSchema = membershipMemberBulkSuspendRequestSchema
export const membershipMemberRoleRequestSchema = z.object({
  orgId: numericId,
  role: z.enum(['admin', 'member', 'viewer']),
  reason: z.string().trim().max(300).nullable().optional(),
}).strict()

// A fresh add answers 201 with replayed:false; a same Idempotency-Key replay answers 200 with replayed:true and the
// original member, so callers can tell a new membership from a retried request.
export const membershipMemberAddResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_MEMBERS_WRITE_CONTRACT_VERSION), memberId: numericId, sessionVersion: z.number().int().nonnegative(),
  replayed: z.boolean(),
}).passthrough()
export const membershipMemberStatusResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_MEMBERS_WRITE_CONTRACT_VERSION),
  member: z.object({ orgId: numericId, userId: numericId, role: z.enum(['org-admin', 'org-member']), status: z.enum(['active', 'suspended']), suspendedReason: z.string().nullable() }).strict(),
  sessionVersion: z.number().int().nonnegative(), grantVersion: z.number().int().nonnegative(),
}).passthrough()
export const membershipMemberRemoveResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_MEMBERS_WRITE_CONTRACT_VERSION), removed: z.boolean(), orgId: numericId, userId: numericId,
  sessionVersion: z.number().int().nonnegative(), grantVersion: z.number().int().nonnegative(),
}).passthrough()
export const membershipMemberBulkSuspendResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_MEMBERS_WRITE_CONTRACT_VERSION), suspended: z.array(numericId),
  failed: z.array(z.object({ userId: numericId, reason: z.string().min(1) }).strict()),
}).passthrough()
export const membershipMemberResolveSeatsResponseSchema = membershipMemberBulkSuspendResponseSchema

const memberRoleSummary = z.object({ orgId: numericId, userId: numericId, role: z.enum(['admin', 'member', 'viewer']), status: z.literal('active'), grantVersion: z.number().int().nonnegative() }).strict()
export const membershipMemberRoleResponseSchema = z.object({
  contract_version: z.literal(MEMBERSHIP_MEMBERS_WRITE_CONTRACT_VERSION), serverTime: z.string().datetime(),
  action: z.object({ type: z.literal('org_member_role_update'), auditAction: z.literal('org_member_role_update'), idempotencyKey: z.string().min(1), replayed: z.literal(false) }).strict(),
  member: memberRoleSummary,
  summary: z.object({ orgId: numericId, refreshedAt: z.string().datetime(), updatedMember: memberRoleSummary, members: z.array(memberRoleSummary) }).strict(),
}).passthrough()

export type MembershipMemberAddRequest = z.infer<typeof membershipMemberAddRequestSchema>
export type MembershipMemberStatusRequest = z.infer<typeof membershipMemberStatusRequestSchema>
export type MembershipMemberRemoveRequest = z.infer<typeof membershipMemberRemoveRequestSchema>
export type MembershipMemberBulkSuspendRequest = z.infer<typeof membershipMemberBulkSuspendRequestSchema>
export type MembershipMemberResolveSeatsRequest = z.infer<typeof membershipMemberResolveSeatsRequestSchema>
export type MembershipMemberRoleRequest = z.infer<typeof membershipMemberRoleRequestSchema>
export type MembershipMemberAddResponse = z.infer<typeof membershipMemberAddResponseSchema>
export type MembershipMemberStatusResponse = z.infer<typeof membershipMemberStatusResponseSchema>
export type MembershipMemberRemoveResponse = z.infer<typeof membershipMemberRemoveResponseSchema>
export type MembershipMemberBulkSuspendResponse = z.infer<typeof membershipMemberBulkSuspendResponseSchema>
export type MembershipMemberResolveSeatsResponse = z.infer<typeof membershipMemberResolveSeatsResponseSchema>
export type MembershipMemberRoleResponse = z.infer<typeof membershipMemberRoleResponseSchema>
